import { useState } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "../../state/store";
import { setCertificateUrl, setFailedCertificateDusns } from "../../state/ducks";

import { certificateAction, certificateErrors, strCode } from "../../consts";
import { deviceCertificatesService, notificationService, t } from "../../services";
import { csvUtil } from "../../utils";
import { useCreateDeviceChangeHistoryRecord } from "../deviceChangeHistory/useCreateDeviceChangeHistoryRecord";
import { Paragraph2, TextButton } from "../../components";

export const parseErrorMessage = (err) => {
  const error = err?.response?.data?.error;

  let errorMessage = certificateErrors[error] ?? error ?? err?.toString();

  return errorMessage;
};

const onDownloadCsv = (dusns) => {
  const formattedDusns = dusns.map((dusn) => {
    return { dusn };
  });

  const failedDevicesFile = csvUtil.createCSVFile(formattedDusns);
  csvUtil.downloadCSVFile(failedDevicesFile, "failedDevices");
};

const handleFailedDevices = (failedDevices, certificateUrl) => {
  if (failedDevices && failedDevices?.length) {
    const generateCertificateMessage = certificateUrl
      ? t(strCode.CREATE_CERTIFICATES_SUCCESS_INFO)
      : null;

    const failedDevicesMessage =
      failedDevices.length > 1 ? strCode.INCORRECT_DEVICES_INFO : strCode.INCORRECT_DEVICE_INFO;

    const notificationData = {
      title: t(
        certificateUrl ? strCode.CREATE_CERTIFICATES_SUCCESS : strCode.CREATE_CERTIFICATES_ERROR
      ),
      message: "",
      Component: (
        <Paragraph2>
          {generateCertificateMessage}
          <p>
            {t(failedDevicesMessage, {
              dusn: failedDevices[0],
              count: failedDevices.length
            })}
          </p>

          {failedDevices.length > 1 && (
            <TextButton
              style={{ height: "24px", textDecoration: "underline" }}
              onClick={() => onDownloadCsv(failedDevices)}>
              {t(strCode.DONWLOAD_LIST_BTN)}
            </TextButton>
          )}
        </Paragraph2>
      )
    };
    if (certificateUrl) {
      notificationService.showSuccess(notificationData);
    } else {
      notificationService.showError(notificationData);
    }
  }

  return failedDevices;
};

export const useCreateCertificates = () => {
  const certificateUrl = useSelector((store) => store.fleetManagement?.certificateUrl);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { mutationCallback: createDeviceChangeHistoryRecord } =
    useCreateDeviceChangeHistoryRecord();

  const mutationCallback = async (dusns, { override = false, shouldFetchExisting } = {}) => {
    setLoading(true);
    setError(false);
    dispatch(setCertificateUrl(null));
    dispatch(setFailedCertificateDusns([]));

    try {
      const res = await deviceCertificatesService.generateCertificates(dusns, override);

      const certificateUrl = res?.data?.data?.url;
      const failedDevices = res?.data?.data?.failed_devices;
      const generatedDevices = res?.data?.data?.generated_devices;

      if (!shouldFetchExisting) {
        if (certificateUrl && !failedDevices?.length) {
          notificationService.showSuccess({
            title: t(strCode.CREATE_CERTIFICATES_SUCCESS),
            message: t(strCode.CREATE_CERTIFICATES_SUCCESS_INFO)
          });
        }

        if (failedDevices?.length) {
          handleFailedDevices(failedDevices, certificateUrl);
          dispatch(setFailedCertificateDusns(failedDevices));
        }
      }

      if (!certificateUrl) {
        setError(true);
      } else {
        if (override) {
          createDeviceChangeHistoryRecord(
            dusns?.filter((dusn) => !failedDevices.includes(dusn)),
            certificateAction.GENERATE
          );
        } else {
          if (generatedDevices?.length) {
            createDeviceChangeHistoryRecord(generatedDevices, certificateAction.GENERATE);
          }
        }
      }

      return certificateUrl;
    } catch (err) {
      setError(true);

      notificationService.showError({
        title: t(strCode.CREATE_CERTIFICATES_ERROR),
        message: t(parseErrorMessage(err))
      });
    } finally {
      setLoading(false);
    }
  };

  return { mutationCallback, data: certificateUrl, loading, error };
};
